import DiagonalArrow from '@/assets/images/Diagonal Arrow-01.png';
import logo from '@/assets/images/Growdash FINAL LOGO-02 (1).png';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import translateURL from '@/lib/translateUrl';
import { Page } from '@growdash/cms-client/generated';
import { Link, useFetcher } from '@remix-run/react';
import { Facebook, Instagram, Linkedin } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type NewsletterResponse = {
  success: boolean;
  error?: string;
};

type FooterProps = {
  pages?: Page[];
  locale: string;
};

export default function Footer({ pages, locale }: FooterProps) {
  const { t } = useTranslation();
  const fetcher = useFetcher<NewsletterResponse>();
  const formRef = useRef<HTMLFormElement>(null);
  const isSubmitting = fetcher.state === 'submitting';
  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.success) {
      formRef.current?.reset();
    }
  }, [fetcher]);

  return (
    <footer className='bg-white border-t py-16 px-4'>
      <div className='container mx-auto max-w-6xl'>
        <div className='grid md:grid-cols-5 gap-10'>
          <div className='col-span-2 md:col-span-1'>
            <Link to='/' className='flex items-center gap-2 mb-4'>
              <img
                src={DiagonalArrow}
                alt={t('footer.logoAltMobile')}
                className='w-8 h-8 md:hidden' // Only show on mobile
              />
              <img
                src={logo}
                alt={t('footer.logoAltDesktop')}
                className='hidden md:block h-[35px]' // Only show on desktop
              />
            </Link>
            <p className='text-sm text-gray-600'>{t('footer.tagline')}</p>
          </div>

          <div>
            <h3 className='font-semibold mb-4'>{t('footer.discover.title')}</h3>
            <ul className='space-y-2'>
              <li>
                <Link
                  to={translateURL(locale, '/')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  {t('footer.discover.home')}
                </Link>
              </li>
              <li>
                <Link
                  to={translateURL(locale, '/about')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  {t('footer.discover.about')}
                </Link>
              </li>
              <li>
                <Link
                  to={translateURL(locale, '/blog')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  {t('footer.discover.blog')}
                </Link>
              </li>
              <li>
                <Link
                  to={translateURL(locale, '/restaurant-index')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  {t('footer.discover.restaurantIndex')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className='font-semibold mb-4'>{t('footer.company.title')}</h3>
            <ul className='space-y-2'>
              <li>
                <Link
                  to={translateURL(locale, '/contact')}
                  className='text-sm text-gray-600 hover:text-gray-900'
                >
                  {t('footer.company.contact')}
                </Link>
              </li>
              {pages?.map(page => (
                <li key={page.id}>
                  <Link
                    to={translateURL(page.locale || '', `/page/${page.slug}`)}
                    className='text-sm text-gray-600 hover:text-gray-900'
                  >
                    {page.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className='font-semibold mb-4'>{t('footer.social.title')}</h3>
            <ul className='space-y-2'>
              <li>
                <a
                  href='https://www.linkedin.com/company/growdash'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2'
                >
                  <Linkedin size={16} />
                  {t('footer.social.linkedin')}
                </a>
              </li>
              <li>
                <a
                  href='https://www.instagram.com/mygrowdash/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2'
                >
                  <Instagram size={16} />
                  {t('footer.social.instagram')}
                </a>
              </li>
              <li>
                <a
                  href='https://www.facebook.com/mygrowdash'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2'
                >
                  <Facebook size={16} />
                  {t('footer.social.facebook')}
                </a>
              </li>
            </ul>
          </div>

          <div className='col-span-2 md:col-span-1'>
            <h3 className='font-semibold mb-4'>
              {t('footer.newsletter.title')}
            </h3>
            <fetcher.Form
              ref={formRef}
              method='post'
              action='/api/newsletter'
              className='flex flex-col gap-2'
            >
              <Input
                name='email'
                type='email'
                placeholder={t('footer.newsletter.placeholder')}
                className='w-full'
                required
                disabled={isSubmitting}
              />
              <Button type='submit' className='w-full' disabled={isSubmitting}>
                {isSubmitting
                  ? t('footer.newsletter.button.subscribing')
                  : t('footer.newsletter.button.subscribe')}
              </Button>
            </fetcher.Form>
            {fetcher.data?.success && (
              <p className='text-xs text-green-600 mt-2'>
                {t('footer.newsletter.success')}
              </p>
            )}
            {fetcher.data?.error && (
              <p className='text-xs text-red-600 mt-2'>{fetcher.data.error}</p>
            )}
            <p className='text-xs text-gray-500 mt-2'>
              {t('footer.newsletter.info')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
